import React, { useState, useEffect } from 'react';
import { array, bool, func, string } from 'prop-types';

import { get, isEmpty } from 'lodash';

import { connect } from 'react-redux';

import { useFormik } from 'formik';
import * as Yup from 'yup';

import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';

import EsmsDialog from '../../components/EsmsDialog';

import * as ArchitectActions from '../../actions/architectActions';
import * as ApplicationMessageActions from '../../actions/applicationMessageActions';
import * as SiteActions from '../../actions/siteActions';
import * as UiActions from '../../actions/uiActions';

import classes from './CreateEntityModal.module.css';

const ValidationSchema = Yup.object().shape({
    client: Yup.string()
        .required(),
    site: Yup.string()
        .required(),
    name: Yup.string()
        .required(),
    equipmentType: Yup.string()
        .required(),
    manufacturer: Yup.string(),
    modelNumber:  Yup.string(),
    serialNumber: Yup.string()
});

const CreateEquipmentModal = ({
    clearErrors,
    clients,
    close,
    companyInfo,
    equipmentTypes,
    errors,
    fetchClientSites,
    isLoading,
    manufacturers,
    open,
    selectedClient,
    selectedSite,
    sites,
    title,
    toggleLoading,
    ...props
}) => {

    const [clientSelected, setClientSelected] = useState(false);

    useEffect(() => {
        if (sites.length > 0) {
            setClientSelected(true);
        } else {
            setClientSelected(false);
        }
    }, [sites]);

    const formik = useFormik({
        initialValues: {
            client:        get(selectedClient, 'id', ''),
            site:          get(selectedSite, 'id', ''),
            name:          '',
            equipmentType: '',
            manufacturer:  '',
            modelNumber:   '',
            serialNumber:  ''
        },
        validationSchema:   ValidationSchema,
        enableReinitialize: true,
        onSubmit:           async (values, { setSubmitting }) => {
            const {
                createEquipment
            } = props;

            let isCreated;

            const siteId = values.site;

            const body = {
                name:            values.name,
                equipmentTypeId: values.equipmentType,
                model:           values.modelNumber,
                serialNumber:    values.serialNumber
            };

            if (!isEmpty(values.manufacturer)) body.equipmentManufacturerId = values.manufacturer;

            clearErrors();
            setSubmitting(true);

            try {
                if (!isLoading) {
                    toggleLoading(true);
                    isCreated = await createEquipment(siteId, body);
                }

                if (isCreated) {
                    setSubmitting(false);
                    toggleLoading(false);

                    handleClose();
                } else {
                    setSubmitting(false);
                    toggleLoading(false);
                }
            } catch (error) {
                setSubmitting(false);
            }
        }
    });

    const handleClientChange = async event => {
        const { value } = event.target;

        formik.setFieldValue('site', '', false);

        formik.setTouched({
            site: false
        });

        setClientSelected(true);

        toggleLoading(false);
        await fetchClientSites(value);

        formik.handleChange(event);
    };

    const handleClose = () => {
        toggleLoading(false);
        clearErrors();

        setClientSelected(false);
        close();
    };

    return (
        <EsmsDialog
            errors={errors}
            open={open}
            onClose={handleClose}
            title={title}
            onRendered={formik.handleReset}
            onConfirm={formik.handleSubmit}
            disabled={formik.isSubmitting}
            isLoading={isLoading}
            confirmText="Create"
        >
            <div className={classes.formColumn}>
                <div className={classes.labelColumn}>
                    <div className={classes.labelHeader}>
                        <img src="/assets/images/equipmentTreeIcon.svg" alt="new equipment icon" />
                        <span>New Equipment</span>
                    </div>
                    <div className={classes.labelContent}>
                        <p>
                            Select the Client and Site to associate equipment with.
                        </p>
                        <p>
                            Choose a unique name for this equipment.
                        </p>
                        <p>
                            Select the equipment type.
                        </p>
                        <p>
                            Select a manufacturer. (Optional)
                        </p>
                        <p>
                            Enter a model number and a serial number. (Optional)
                        </p>
                    </div>
                </div>
                <div className={classes.inputColumn}>
                    <div className={classes.inputs}>
                        <FormControl className={classes.inputWrapper}>
                            <InputLabel
                                htmlFor="client"
                                classes={{
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.label
                                }}
                                error={Boolean(formik.errors.client) && Boolean(formik.touched.client)}
                            >
                                Client
                            </InputLabel>
                            <Select
                                value={formik.values.client}
                                onChange={handleClientChange}
                                className={classes.select}
                                inputProps={{
                                    name:   'client',
                                    id:     'client',
                                    onBlur: formik.handleBlur
                                }}
                                error={Boolean(formik.errors.client) && Boolean(formik.touched.client)}
                            >
                                {
                                    clients.map(type =>
                                        <MenuItem
                                            key={type.id}
                                            value={type.id}
                                        >
                                            {type.name}
                                        </MenuItem>
                                    )
                                }
                            </Select>
                            {formik.touched.client &&
                             formik.errors.client && <FormHelperText className={classes.error}>{formik.errors.client}</FormHelperText>}
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <InputLabel
                                htmlFor="site"
                                classes={{
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.label
                                }}
                                error={Boolean(formik.errors.site) && Boolean(formik.touched.site)}
                            >
                                Site
                            </InputLabel>
                            <Select
                                value={formik.values.site}
                                onChange={formik.handleChange}
                                className={classes.select}
                                disabled={!clientSelected}
                                inputProps={{
                                    name:   'site',
                                    id:     'site',
                                    onBlur: formik.handleBlur
                                }}
                                error={Boolean(formik.errors.site) && Boolean(formik.touched.site)}
                            >
                                {
                                    sites.map(type =>
                                        <MenuItem
                                            key={type.id}
                                            value={type.id}
                                        >
                                            {type.name}
                                        </MenuItem>
                                    )
                                }
                            </Select>
                            {formik.touched.site &&
                             formik.errors.site && <FormHelperText className={classes.error}>{formik.errors.site}</FormHelperText>}
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <TextField
                                id="name"
                                name="name"
                                type="text"
                                value={formik.values.name}
                                label="Name"
                                onChange={formik.handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                FormHelperTextProps={{
                                    classes: {
                                        error: classes.error
                                    }
                                }}
                                error={Boolean(formik.errors.name) && Boolean(formik.touched.name)}
                                helperText={Boolean(formik.touched.name) && formik.errors.name}
                            />
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <InputLabel
                                htmlFor="equipmentType"
                                classes={{
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.label
                                }}
                                error={Boolean(formik.errors.equipmentType) && Boolean(formik.touched.equipmentType)}
                            >
                                Equipment Type
                            </InputLabel>
                            <Select
                                value={formik.values.equipmentType}
                                onChange={formik.handleChange}
                                className={classes.select}
                                inputProps={{
                                    name:   'equipmentType',
                                    id:     'equipmentType',
                                    onBlur: formik.handleBlur
                                }}
                                error={Boolean(formik.errors.equipmentType) && Boolean(formik.touched.equipmentType)}
                            >
                                {
                                    equipmentTypes.map(type =>
                                        <MenuItem
                                            key={type.id}
                                            value={type.id}
                                        >
                                            {type.name}
                                        </MenuItem>
                                    )
                                }
                            </Select>
                            {formik.touched.equipmentType &&
                             formik.errors.equipmentType && <FormHelperText className={classes.error}>{formik.errors.equipmentType}</FormHelperText>}
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <InputLabel
                                htmlFor="manufacturer"
                                classes={{
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.label
                                }}
                                error={Boolean(formik.errors.manufacturer) && Boolean(formik.touched.manufacturer)}
                            >
                                Manufacturer
                            </InputLabel>
                            <Select
                                value={formik.values.manufacturer}
                                onChange={formik.handleChange}
                                className={classes.select}
                                inputProps={{
                                    name:   'manufacturer',
                                    id:     'manufacturer',
                                    onBlur: formik.handleBlur
                                }}
                                error={Boolean(formik.errors.manufacturer) && Boolean(formik.touched.manufacturer)}
                            >
                                {
                                    manufacturers.map(manufacturer =>
                                        <MenuItem
                                            key={manufacturer.id}
                                            value={manufacturer.id}
                                        >
                                            {manufacturer.name}
                                        </MenuItem>
                                    )
                                }
                            </Select>
                            {formik.touched.manufacturer &&
                             formik.errors.manufacturer && <FormHelperText className={classes.error}>{formik.errors.manufacturer}</FormHelperText>}
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <TextField
                                id="modelNumber"
                                name="modelNumber"
                                type="text"
                                value={formik.values.modelNumber}
                                label="Model Number"
                                onChange={formik.handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                FormHelperTextProps={{
                                    classes: {
                                        error: classes.error
                                    }
                                }}
                                error={Boolean(formik.errors.modelNumber) && Boolean(formik.touched.modelNumber)}
                                helperText={Boolean(formik.touched.modelNumber) && formik.errors.modelNumber}
                            />
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <TextField
                                id="serialNumber"
                                name="serialNumber"
                                type="text"
                                value={formik.values.serialNumber}
                                label="Serial Number"
                                onChange={formik.handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                FormHelperTextProps={{
                                    classes: {
                                        error: classes.error
                                    }
                                }}
                                error={Boolean(formik.errors.serialNumber) && Boolean(formik.touched.serialNumber)}
                                helperText={Boolean(formik.touched.serialNumber) && formik.errors.serialNumber}
                            />
                        </FormControl>
                    </div>
                </div>
            </div>
        </EsmsDialog>
    );
};

CreateEquipmentModal.defaultProps = {
    clients:        [],
    equipmentTypes: [],
    manufacturers:  [],
    sites:          []
};

CreateEquipmentModal.propTypes = {
    clients:        array,
    close:          func.isRequired,
    equipmentTypes: array,
    manufacturers:  array,
    open:           bool.isRequired,
    sites:          array,
    title:          string.isRequired
};

const mapStateToProps = state => {
    return {
        clients:        state.architect.clientNodes,
        companyInfo:    state.companyInfo,
        equipmentTypes: state.equipmentTypes.data,
        errors:         state.messages.errors,
        isLoading:      state.ui.isLoading,
        selectedClient: state.client.selectedClient,
        selectedSite:   state.site.selectedSite,
        manufacturers:  state.architect.manufacturers,
        sites:          state.site.sites
    };
};

const mapDispatchToProps = {
    ...ArchitectActions,
    ...ApplicationMessageActions,
    ...SiteActions,
    ...UiActions
};

export default connect(mapStateToProps, mapDispatchToProps)(CreateEquipmentModal);
